import {
	faEye,
	faFloppyDisk,
	faPen,
	faPlus,
	faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { isArray } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { generateUniqueId } from "../../../../../common/generateUniqueId";
import ImportMemories from "../../../../../common/import/ImportMemories";
import { nonNullableFilter } from "../../../../../common/nonNullableFilter";
import { env } from "../../../../../env/env";
import {
	createDeviceSlot,
	deleteMemories,
	deleteMemory,
	insertMemory,
	selectDevices,
	selectMemories,
	updateDeviceSlot,
	updateMemory,
	updateMemoryRefSource,
} from "../../../../editor/editorSlice";
import Memory, {
	MemoryScratchPad,
	MemorySource,
	MemorySourceAddress,
	MemorySourceIo,
	isSameMemorySource,
} from "../../../../editor/interfaces/components/Memory";
import {
	Types,
	VariableContent,
	variableFromLogics,
} from "../../../../editor/interfaces/components/Variable";
import { LogicsMemory as MemoryConfig } from "../../../../logics/logics/LogicsMemory";
import { selectLanguages, selectLogics } from "../../../../logics/logicsSlice";
import { WindowTypeAndPropsDefinition } from "../../../windowManagerSlice";
import {
	BooleanVariable,
	DataField,
	DataImageField,
	EnumVariable,
	ImageValues,
	NumberVariable,
} from "../BlockConfigWindow/BlockConfigWindow";
import css from "./MemoryManagerWindow.module.css";

export interface MemoryManagerWindowProps {
	initialCategory?: string;
	linkToMemoryRef?: {
		sheetId: string;
		memoryRefId: string;
	};
}

export type MemoryManagerWindowTypeAndProps = WindowTypeAndPropsDefinition<
	"memoryManager",
	MemoryManagerWindowProps
>;

function createMemoryScratchPad(config: MemoryConfig) {
	const memory: MemoryScratchPad = {
		label: config.name,
		name: config.name,
		broadcast: false,
		source: null,
		metadata: {
			variables: Object.fromEntries(
				config.variables.map((variable) => [
					variable.name,
					variableFromLogics(variable),
				]),
			),
			data: config.data
				? Object.fromEntries(config.data.map((data) => [data.size, ""]))
				: {},
		},
	};

	return memory;
}

export function MemoryManagerWindow(
	props: MemoryManagerWindowProps & { id: string },
) {
	const dispatch = useAppDispatch();
	const sidebarRef = useRef<HTMLInputElement>(null);

	const logics = useAppSelector(selectLogics);
	const editorMemories = useAppSelector(selectMemories);
	const editorDevices = useAppSelector(selectDevices);
	const languages = useAppSelector(selectLanguages);

	const { t, i18n } = useTranslation();

	const [selectedCategory, setSelectedCategory] = useState<string | undefined>(
		props.initialCategory,
	);
	const [searchBoxText, setSearchBoxText] = useState<string>("");
	const [memoryScratchPad, setMemoryScratchPad] =
		useState<MemoryScratchPad | null>(null);
	const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
	const [isEditing, setIsEditing] = useState<boolean>(false);
	const [unableToSaveMessage, setUnableToSaveMessage] = useState<string | null>(
		null,
	);
	const [languageIndex, setLanguageIndex] = useState<number>(0);
	const [selectAll, setSelectAll] = useState<boolean>(false);
	const [selectedMemories, setSelectedMemories] = useState<
		Record<number, boolean>
	>({});

	useEffect(() => {
		if (selectedCategory && selectedIndex !== null) {
			const category = editorMemories[selectedCategory];

			if (selectedIndex >= category.length) {
				if (!isEditing) {
					setMemoryScratchPad(null);
				}
				setSelectedIndex(null);
				return;
			}

			const source = category[selectedIndex].source;

			const availableDeviceIds = editorDevices
				.filter(nonNullableFilter)
				.map((device) => device.id);

			if (
				source.type !== "local" &&
				availableDeviceIds.includes(source.deviceId)
			) {
				if (!isEditing) {
					setMemoryScratchPad(editorMemories[selectedCategory][selectedIndex]);
				}
			} else {
				if (!isEditing) {
					setMemoryScratchPad(null);
				}
				setSelectedIndex(null);
			}
		}
	}, [editorDevices]);

	const memoryCategories = Object.entries(logics.memory)
		.sort((a, b) => a[1].hexId.localeCompare(b[1].hexId))
		.map(([key, _]) => key);

	const config = selectedCategory ? logics.memory[selectedCategory] : undefined;
	const nonPrivateVariables = config?.variables.filter(
		(variable) => !variable.private,
	);
	const allVariables = config?.variables;
	const isIo = !!(config && ["INPUT", "OUTPUT"].includes(config.name));

	const hasImage = config?.data?.find((data) => data.type == "file");

	const setVariableContent = (name: string) => {
		return (content: VariableContent) =>
			setMemoryScratchPad((memory) => {
				if (memory === null) {
					return null;
				}

				return {
					...memory,
					metadata: {
						...memory.metadata,
						variables: {
							...memory.metadata.variables,
							[name]: {
								...memory.metadata.variables[name],
								content,
							},
						},
					},
				};
			});
	};

	const setDataContent = (name: string, index?: number, language?: string) => {
		return (content: string) =>
			setMemoryScratchPad((memory) => {
				let input: string | string[] = content;

				if (memory === null) {
					return null;
				}

				if (index != undefined) {
					const position = [...memory.metadata.data[name]];
					position[index] = content;
					input = position;
				}

				return {
					...memory,

					metadata: {
						...memory.metadata,

						variables: {
							...memory.metadata.variables,
							[name]: {
								...memory.metadata.variables[name],
								content: {
									type: "number",
									value: content.length,
								},
							},
						},
						data: {
							...memory.metadata.data,
							[name]: input,
							...(language != undefined ? { [language]: content } : {}),
						},
					},
				};
			});
	};

	const setDataImageContent = (name: string) => {
		return (content: ImageValues) =>
			setMemoryScratchPad((memory) => {
				const { start_x, start_y, end_x, end_y, size } = content;

				if (memory === null) {
					return null;
				}

				return {
					...memory,
					metadata: {
						...memory.metadata,
						variables: {
							...memory.metadata.variables,
							...(start_x != undefined
								? {
										start_x: {
											...memory.metadata.variables.start_x,
											content: {
												type: "number",
												value: start_x,
											},
										},
									}
								: {}),
							...(start_y != undefined
								? {
										start_y: {
											...memory.metadata.variables.start_y,
											content: {
												type: "number",
												value: start_y,
											},
										},
									}
								: {}),
							...(end_x != undefined
								? {
										end_x: {
											...memory.metadata.variables.end_x,
											content: {
												type: "number",
												value: end_x,
											},
										},
									}
								: {}),
							...(end_y != undefined
								? {
										end_y: {
											...memory.metadata.variables.end_y,
											content: {
												type: "number",
												value: end_y,
											},
										},
									}
								: {}),
							...(size != undefined
								? {
										[name]: {
											...memory.metadata.variables[name],
											content: {
												type: "number",
												value: size,
											},
										},
									}
								: {}),
						},
						...(content?.data != undefined
							? {
									data: {
										...memory.metadata.data,
										image_size: content?.data,
									},
								}
							: {}),
					},
				};
			});
	};

	const setSourceDeviceId = (deviceId: string) => {
		setMemoryScratchPad((memory) => {
			if (memory === null) {
				return null;
			}

			let source: MemorySource | null = null;

			if (deviceId === "null") {
				source = {
					type: "local",
				};
			} else {
				if (isIo) {
					source = {
						type: "io",
						deviceId,
						io: "",
					};
				} else {
					const originalSource =
						selectedIndex !== null
							? editorMemories[memory.name][selectedIndex].source
							: null;

					if (originalSource === null || originalSource.type !== "address") {
						const category = editorMemories[memory.name]
							.map((memory) => memory.source)
							.filter(nonNullableFilter)
							.filter((source) => "address" in source) as MemorySourceAddress[];

						const existingAddresses = category
							.map((source) => source.address)
							.toSorted((a, b) => a - b);
						let address = existingAddresses.findIndex(
							(address, index) => address !== index,
						);

						if (address === -1) {
							address = existingAddresses.length;
						}

						source = {
							type: "address",
							deviceId,
							address,
						};
					} else {
						source = {
							type: "address",
							deviceId,
							address: originalSource.address,
						};
					}
				}
			}

			return {
				...memory,
				source,
			};
		});
	};

	const setSourceAddress = (address: number) => {
		setMemoryScratchPad((memory) => {
			if (memory === null) {
				return null;
			}

			const originalSource = memory.source as MemorySourceAddress;

			return {
				...memory,
				source: {
					...originalSource,
					address,
				},
			};
		});
	};

	const setSourceIo = (io: string) => {
		setMemoryScratchPad((memory) => {
			if (memory === null) {
				return null;
			}

			const originalSource = memory.source as MemorySourceIo;

			return {
				...memory,
				source: {
					...originalSource,
					io,
				},
			};
		});
	};

	const canSaveMemory = useMemo(() => {
		if (memoryScratchPad === null) {
			setUnableToSaveMessage("Memory is not defined");
			return false;
		}

		if (memoryScratchPad.source === null) {
			setUnableToSaveMessage("Memory source is not defined");
			4;
			return false;
		}

		// Check if label is valid
		const existingMemoryNames = Object.keys(editorMemories);
		const existingMemoryLabels = Object.values(editorMemories).flatMap(
			(memories) => memories.map((memory) => memory.label),
		);

		const isLabelAMemoryName = existingMemoryNames.includes(
			memoryScratchPad.label,
		);

		if (isLabelAMemoryName) {
			setUnableToSaveMessage("Memory label can't be a memory name");
			return false;
		}

		const isCurrentLabelInUse = existingMemoryLabels.includes(
			memoryScratchPad.label,
		);

		if (selectedIndex !== null) {
			const selfMemoryLabel =
				editorMemories[memoryScratchPad.name][selectedIndex].label;
			if (isCurrentLabelInUse && selfMemoryLabel !== memoryScratchPad.label) {
				setUnableToSaveMessage("Memory label is already in use");
				return false;
			}
		} else {
			if (isCurrentLabelInUse) {
				setUnableToSaveMessage("Memory label is already in use");
				return false;
			}
		}

		if (config === undefined) {
			setUnableToSaveMessage("[INTERNAL ERROR] Memory category is not defined");
			return false;
		}

		const logicsVariables = config.variables;
		const areVariablesValid = Object.entries(
			memoryScratchPad.metadata.variables,
		).every(([name, variable]) => {
			switch (variable.content.type) {
				case "boolean":
				case "enum":
					return true;
				case "number":
					if (isNaN((variable.content as Types.Number).value)) {
						return false;
					}

					const config = logicsVariables.find(
						(variable) => variable.name === name,
					);

					if (config === undefined) {
						setUnableToSaveMessage(
							"[INTERNAL ERROR] Variable config is not defined",
						);
						return false;
					}

					const configContent = config.content;

					if (configContent.type !== "number") {
						setUnableToSaveMessage(
							"[INTERNAL ERROR] Variable config is not a number",
						);
						return false;
					}

					if (
						configContent.min !== undefined &&
						(variable.content as Types.Number).value < configContent.min
					) {
						setUnableToSaveMessage(
							`Variable "${config.label}" value is below minimum`,
						);
						return false;
					}

					if (
						configContent.max !== undefined &&
						(variable.content as Types.Number).value > configContent.max
					) {
						setUnableToSaveMessage(
							`Variable "${config.label}" value is above maximum`,
						);
						return false;
					}

					return true;
			}
		});

		if (!areVariablesValid) {
			return false;
		}

		// Check if source is valid
		if (memoryScratchPad.source.type !== "local") {
			if (memoryScratchPad.source.type === "io") {
				if (memoryScratchPad.source.io === "") {
					setUnableToSaveMessage("IO is not selected");
					return false;
				}
			} else {
				if (isNaN(memoryScratchPad.source.address)) {
					setUnableToSaveMessage("Address is not a number");
					return false;
				}
			}

			const source = memoryScratchPad.source;

			const sourceIsTaken = Object.values(editorMemories).some((memories) =>
				memories.some(
					(memory) =>
						isSameMemorySource(memory.source, source) &&
						memory.name === memoryScratchPad.name,
				),
			);

			if (selectedIndex !== null) {
				const selfSource =
					editorMemories[memoryScratchPad.name][selectedIndex].source;
				if (sourceIsTaken && !isSameMemorySource(selfSource, source)) {
					const takenBy =
						editorMemories[memoryScratchPad.name].find((memory) =>
							isSameMemorySource(memory.source, source),
						)?.label ?? "unknown";
					setUnableToSaveMessage(
						`Memory source is already in use by "${takenBy}"`,
					);
					return false;
				}
			} else {
				if (sourceIsTaken) {
					setUnableToSaveMessage(
						`Memory source is already in use by another memory`,
					);
					return false;
				}
			}
		}

		setUnableToSaveMessage(null);
		return true;
	}, [memoryScratchPad]);

	const download = (content: any, fileName: string, contentType: string) => {
		let a = document.createElement("a");
		let file = new Blob([content], {
			type: contentType,
		});
		a.href = URL.createObjectURL(file);
		a.download = fileName;
		a.click();
	};

	const importMemories = () => {
		const input = document.createElement("input");
		input.name = "import-memories";
		input.type = "file";
		input.accept = ".json";

		const logicsDevice = logics.device;
		const logicsMemory = logics.memory;

		input.onchange = (event) => {
			const file = (event.target as HTMLInputElement).files?.[0];

			if (!file) {
				console.warn(`Error reading file.`);
				return;
			}

			const reader = new FileReader();
			reader.readAsArrayBuffer(file);

			reader.onload = (event) => {
				const content = event.target?.result;

				if (!content) {
					console.warn(`Error reading file contents.`);
					return;
				}

				if (typeof content === "string") {
					console.warn(`File content read as string.`);
					return;
				}

				const decoder = new TextDecoder("utf-8");
				const decodedString = decoder.decode(content);
				const data = JSON.parse(decodedString) as ImportMemories;

				if (!data.devices || !data.memories) {
					toast.error(t("invalidImportMemories"));
					input.remove();
					return;
				}

				const { devices, memories } = data;

				let matchDevices: Record<string, string> = {};
				const nonCreatedDevices: string[] = [];
				const nonCreatedMemories: string[] = [];

				let lastIndex = editorDevices.length;

				const filteredDevices = devices.filter((device) =>
					memories.some((memo) => memo.source?.deviceId == device.id),
				);

				filteredDevices.forEach((device) => {
					const logicDevice = logicsDevice[device!.label];
					const alreadyHasDevice = editorDevices.find((dev) => {
						return dev?.address === device.address;
					});

					if (alreadyHasDevice) {
						matchDevices = {
							...matchDevices,
							[device.id]: alreadyHasDevice.id,
						};
						nonCreatedDevices.push(
							`${alreadyHasDevice.name} - ${alreadyHasDevice.address}`,
						);
						return;
					}

					dispatch(createDeviceSlot());

					device!.outputs = Object.entries(logicDevice.output).map(
						([key, value], idx) => {
							const output = device?.outputs.find((o) => o.name === key);

							if (output) {
								const { name } = output;
								const { id, ...newVariables } = value;

								return {
									id: idx,
									name,
									...Object.entries(newVariables).reduce(
										(acc, [k, variable]) => {
											return {
												...acc,
												...(output[k] === undefined
													? {
															[k]: isArray(variable) ? variable[0] : variable,
														}
													: { [k]: output[k] }),
											};
										},
										{},
									),
								};
							} else {
								const { id, ...rest } = value;

								return {
									id: idx,
									name: key,
									...Object.entries(rest).reduce(
										(init, [k, v]) => ({
											...init,
											[k]: Array.isArray(v) ? v[0] : v,
										}),
										{},
									),
								};
							}
						},
					);

					device!.inputs = Object.entries(logicDevice.input).map(
						([key, value], idx) => {
							const input = device?.inputs.find((o) => o.name === key);

							if (input) {
								const { name } = input;
								const { id, ...newVariables } = value;

								return {
									id: id,
									name,
									...Object.entries(newVariables).reduce(
										(acc, [k, variable]) => {
											return {
												...acc,
												...(input[k] === undefined
													? {
															[k]: isArray(variable) ? variable[0] : variable,
														}
													: { [k]: input[k] }),
											};
										},
										{},
									),
								};
							} else {
								const { id, ...rest } = value;

								return {
									id: idx,
									name: key,
									...Object.entries(rest).reduce(
										(init, [k, v]) => ({
											...init,
											[k]: Array.isArray(v) ? v[0] : v,
										}),
										{},
									),
								};
							}
						},
					);

					dispatch(
						updateDeviceSlot({
							index: lastIndex,
							device,
						}),
					);

					lastIndex++;
				});

				memories.forEach((memo) => {
					const alreadyHasMemory = editorMemories[memo.name].find((m) => {
						switch (m.source.type) {
							case "address":
								return (
									memo.source.type == "address" &&
									m.source.address == memo.source.address &&
									m.source.deviceId == memo.source.deviceId
								);
							case "io":
								return (
									m.source.type == "io" &&
									memo.source.type == "io" &&
									matchDevices[m.source.deviceId] == memo.source.deviceId &&
									m.source.io == memo.source.io
								);
							case "local":
								return m.id == memo.id && m.label == m.label;
						}
					});

					if (alreadyHasMemory) {
						if (memo.source.type == "io") {
							nonCreatedMemories.push(
								`${memo.label} | ${memo.source.deviceId} - ${memo.source.io}`,
							);
						} else if (memo.source.type == "address") {
							nonCreatedMemories.push(
								`${memo.label} | ${memo.source.deviceId} - ${memo.source.address}`,
							);
						} else {
							nonCreatedMemories.push(`${memo.label} | ${memo.source.type}`);
						}
						return;
					}

					const foundId = editorMemories[memo.name].find(
						(m) => m.id == memo.id,
					);

					const newVariables = logicsMemory[memo.name].variables.reduce(
						(acc, item) => {
							return {
								...acc,
								[item.name]: memo.metadata.variables[item.name]
									? memo.metadata.variables[item.name]
									: {
											label: item.label,
											content: {
												type: item.content.type,
												value: item.content.default,
											},
										},
							};
						},
						{},
					);

					dispatch(
						insertMemory({
							memory: {
								...memo,
								...(foundId ? { id: generateUniqueId("memory") } : {}),
								metadata: {
									...memo.metadata,
									variables: newVariables,
								},
							},
						}),
					);
				});

				if (nonCreatedDevices.length || nonCreatedMemories.length) {
					toast.info(
						<div>
							<strong>{t("devicesAlreadyExists")}</strong>
							<ul>
								{nonCreatedDevices.map((device) => (
									<li>{device}</li>
								))}
							</ul>
							<strong>{t("memoriesAlreadyExists")}</strong>
							<ul>
								{nonCreatedMemories.map((memory) => (
									<li>{memory}</li>
								))}
							</ul>
						</div>,
					);
					return;
				}

				toast.success(t("importSuccess"));
			};
		};

		input.click();
	};

	const exportMemories = async () => {
		if (!selectedCategory) {
			toast.error(t("exportError"));
			return;
		}

		let memories = editorMemories[selectedCategory];

		if (Object.keys(selectedMemories).length > 0) {
			memories = editorMemories[selectedCategory].filter(
				(_, index) => selectedMemories[index],
			);
		}

		const packet = {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				devices: editorDevices,
				memories: memories,
			}),
		};

		const fetch_result = await fetch(`${env.apiUrl}/export-memories`, packet);

		if (fetch_result.ok) {
			download(await fetch_result.blob(), `${selectedCategory}.json`, "blob");
		}
	};

	const changeMemoriesSelect = (result: boolean) => {
		if (!selectedCategory) return;

		const allSelected = editorMemories[selectedCategory].reduce(
			(acc, _, index) => {
				return {
					...acc,
					[index]: result,
				};
			},
			{},
		);
		setSelectedMemories(allSelected);
	};

	const deleteSelectedMemories = () => {
		if (!selectedCategory) return;

		let indexes: number[] = [];
		editorMemories[selectedCategory].forEach((_, index) => {
			if (selectedMemories[index]) indexes.push(index);
		});

		dispatch(deleteMemories({ memoryName: selectedCategory, indexes }));
		changeMemoriesSelect(false);
	};

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (!selectedCategory) return;

			if (event.key === "ArrowDown") {
				event.preventDefault();
				const newIndex = Number(selectedIndex) + 1;

				if (newIndex > editorMemories[selectedCategory].length - 1) return;

				setSelectedIndex(newIndex);
				const memory = editorMemories[selectedCategory][newIndex];
				setMemoryScratchPad(memory);

				const memoryRef = document.getElementById(`memory-${newIndex}`);

				if (!sidebarRef || !sidebarRef.current) return;

				if (
					memoryRef?.offsetTop &&
					memoryRef?.offsetTop > sidebarRef.current?.clientHeight
				) {
					sidebarRef.current.scrollBy(0, 30);
				}
			}

			if (event.key === "ArrowUp") {
				event.preventDefault();
				const newIndex = Number(selectedIndex) - 1;

				if (newIndex < 0) return;

				setSelectedIndex(newIndex);
				const memory = editorMemories[selectedCategory][newIndex];
				setMemoryScratchPad(memory);

				const memoryRef = document.getElementById(`memory-${newIndex}`);

				memoryRef?.scrollIntoView();
			}

			if (event.key === "Delete" && Object.keys(selectedMemories).length > 0) {
				toast.info("Deseja excluir as memórias selecionadas?", {
					id: "memories-toast",
					position: "top-center",
					actionButtonStyle: {
						backgroundColor: "#0973b5",
					},
					action: {
						label: "Excluir",
						onClick: () => {
							toast.dismiss("memories-toast");
							deleteSelectedMemories();
						},
					},
					cancel: {
						label: "Cancelar",
						onClick: () => toast.dismiss("memories-toast"),
					},
				});
			}

			if (event.key === "Delete") {
				const inputTags = ["input", "select", "button", "textarea"];
				const activeType = `${document.activeElement?.tagName.toLowerCase()}`;

				if (
					!inputTags.includes(activeType) &&
					memoryScratchPad &&
					selectedIndex
				) {
					setMemoryScratchPad(null);
					setSelectedIndex(null);
					setIsEditing(false);
					dispatch(
						deleteMemory({
							memoryName: memoryScratchPad.name,
							index: selectedIndex,
						}),
					);
				}
			}
		};

		window.addEventListener("keydown", handleKeyDown);

		return () => {
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, [
		editorMemories,
		selectedCategory,
		selectedIndex,
		memoryScratchPad,
		selectedMemories,
	]);

	return (
		<div className={css.content}>
			<div className={css.sideBar}>
				<div className={css.importExportDiv}>
					{selectedCategory && (
						<input
							id="select-all"
							type="checkbox"
							value={`${selectAll}`}
							checked={selectAll}
							onChange={() => {
								setSelectAll(!selectAll);
								changeMemoriesSelect(!selectAll == true);
							}}
						/>
					)}

					<button onClick={importMemories}>{t("import")}</button>
					<button onClick={exportMemories}>{t("export")}</button>
				</div>
				<div className={css.categoryBar}>
					<select
						id="select-memory-category"
						className={classNames(css.categorySelector, css.select)}
						value={selectedCategory}
						onChange={(event) => {
							setSelectedCategory(event.target.value);
							setMemoryScratchPad(null);
							setSelectedIndex(null);
							setSelectedMemories({});
							setIsEditing(false);
							setSelectAll(false);
							changeMemoriesSelect(false);
						}}
					>
						{selectedCategory === undefined && (
							<option value={undefined}></option>
						)}
						{memoryCategories.map((category) => (
							<option key={category} value={category}>
								{category}
							</option>
						))}
					</select>
					<button
						id="create-memory-button"
						className={css.creatorButton}
						onClick={() => {
							if (selectedCategory === undefined) {
								return;
							}

							const category = logics.memory[selectedCategory];
							const memory = createMemoryScratchPad(category);
							setMemoryScratchPad(memory);
							setSelectedIndex(null);
							setIsEditing(true);
						}}
					>
						<FontAwesomeIcon icon={faPlus} />
					</button>
				</div>
				<input
					className={css.searchBox}
					value={searchBoxText}
					type="text"
					placeholder="Search..."
					onChange={(event) => setSearchBoxText(event.target.value)}
				/>

				<div ref={sidebarRef} className={css.memoryList}>
					{selectedCategory &&
						editorMemories[selectedCategory].map((memory, index) => {
							if (
								searchBoxText !== "" &&
								!memory.label
									.toUpperCase()
									.includes(searchBoxText.toUpperCase())
							) {
								return null;
							}

							return (
								<div
									id={`memory-${index}`}
									key={`memory-${index}`}
									className={classNames(css.memoryListItem, {
										[css.memoryListItemSelected]: index === selectedIndex,
									})}
									onClick={() => {
										setMemoryScratchPad(memory);
										setSelectedIndex(index);
										if (index !== selectedIndex) {
											setIsEditing(false);
										}
									}}
								>
									<input
										type="checkbox"
										value={`${selectedMemories[index]}`}
										checked={selectedMemories[index]}
										onClick={(e) => {
											e.stopPropagation();

											if (e.shiftKey && !selectedMemories[index]) {
												const sortedArray = Object.keys(selectedMemories)
													.sort()
													.map((i) => Number(i));
												const minKey = sortedArray[0] || 0;
												const maxKey = sortedArray.at(-1) || 0;

												const diffMin = Math.abs(minKey - index);
												const diffMax = Math.abs(maxKey - index);

												let newSelected = {};

												if (diffMin >= diffMax) {
													for (let i = minKey; i <= index; i++) {
														newSelected = {
															...newSelected,
															[i]: true,
														};
													}
												}

												if (diffMax > diffMin) {
													for (let i = maxKey; i > index; i--) {
														newSelected = {
															...newSelected,
															[i]: true,
														};
													}
												}

												setSelectedMemories(newSelected);

												return;
											}

											setSelectedMemories((old) => {
												return { ...old, [index]: !old[index] };
											});
										}}
									/>
									{memory.label}
								</div>
							);
						})}
				</div>
			</div>
			{memoryScratchPad && config && nonPrivateVariables && (
				<div
					key={`${selectedCategory}-${selectedIndex}`}
					className={css.memoryEditor}
				>
					<div className={css.row}>
						{!isEditing && (
							<>
								<button
									className={css.button}
									onClick={() => {
										setIsEditing(true);
									}}
								>
									<FontAwesomeIcon icon={faPen} />
								</button>
								<div className={css.memoryLabel}>{memoryScratchPad.label}</div>
								{selectedIndex !== null && (
									<button
										className={classNames(css.button, css.deleteButton)}
										onClick={() => {
											setMemoryScratchPad(null);
											setSelectedIndex(null);
											setIsEditing(false);
											dispatch(
												deleteMemory({
													memoryName: memoryScratchPad.name,
													index: selectedIndex,
												}),
											);
										}}
									>
										<FontAwesomeIcon icon={faTrash} />
									</button>
								)}
							</>
						)}
						{isEditing && (
							<>
								<button
									id="save-memory-button"
									className={classNames(css.button, {
										[css.creatorButtonDisabled]: !canSaveMemory,
									})}
									disabled={!canSaveMemory}
									onClick={() => {
										if (!canSaveMemory) {
											throw new Error("Cannot insert memory with invalid data");
										}

										if (memoryScratchPad.source === null) {
											throw new Error("Cannot insert memory with null source");
										}

										if (selectedCategory === undefined) {
											throw new Error(
												"Cannot insert memory in undefined category",
											);
										}

										if (selectedIndex === null) {
											const memoryId = generateUniqueId("memory");
											setSelectedIndex(editorMemories[selectedCategory].length);
											dispatch(
												insertMemory({
													memory: {
														id: memoryId,
														...memoryScratchPad,
													} as Memory,
												}),
											);

											if (props.linkToMemoryRef) {
												dispatch(
													updateMemoryRefSource({
														sheetId: props.linkToMemoryRef.sheetId,
														memoryRefId: props.linkToMemoryRef.memoryRefId,
														sourceMemoryId: memoryId,
													}),
												);
											}
										} else {
											if (
												selectedIndex >= editorMemories[selectedCategory].length
											) {
												throw new Error(
													"Cannot update memory with invalid index",
												);
											}

											dispatch(
												updateMemory({
													memory: {
														id: editorMemories[selectedCategory][selectedIndex]
															.id,
														...memoryScratchPad,
													} as Memory,
													index: selectedIndex,
												}),
											);
										}

										setIsEditing(false);
									}}
								>
									<FontAwesomeIcon icon={faFloppyDisk} />
								</button>
								<MemoryLabel
									scratchPadMemory={memoryScratchPad}
									onChange={(content) => {
										setMemoryScratchPad({
											...memoryScratchPad,
											label: content,
										});
									}}
								/>
							</>
						)}
					</div>
					{unableToSaveMessage && (
						<div
							className={css.row}
							style={{
								color: "var(--palette-red)",
							}}
						>
							{unableToSaveMessage}
						</div>
					)}
					<div className={css.row}>
						<div className={css.sectionTitle}>Variables</div>
					</div>
					{nonPrivateVariables.map((config, index) => {
						const variable = memoryScratchPad.metadata.variables[config.name];

						return (
							<div key={index} className={css.row}>
								<div className={css.label}>{variable.label}</div>
								{isEditing && (
									<>
										{variable.content.type === "boolean" && (
											<BooleanVariable
												content={variable.content}
												config={config}
												onChange={setVariableContent(config.name)}
											/>
										)}
										{variable.content.type === "enum" && (
											<EnumVariable
												content={variable.content}
												config={config}
												onChange={setVariableContent(config.name)}
											/>
										)}
										{variable.content.type === "number" && (
											<NumberVariable
												content={variable.content}
												config={config}
												onChange={setVariableContent(config.name)}
											/>
										)}
									</>
								)}
								{!isEditing && (
									<div className={css.variableValue}>
										{variable.content.type !== "boolean" &&
											variable.content.value}
										{variable.content.type === "boolean" && (
											<div style={{ pointerEvents: "none" }}>
												<BooleanVariable
													content={variable.content}
													config={config}
													onChange={() => {}}
												/>
											</div>
										)}
									</div>
								)}
							</div>
						);
					})}
					{config.data && config.data.length > 0 && (
						<>
							<div className={css.row}>
								<div className={css.sectionTitle}>Data</div>
							</div>
							{config.data.map((config, index) => {
								const currentData = memoryScratchPad.metadata.data[config.size];
								const associatedSizeVariable = allVariables?.find(
									(variable) => variable.name === config.size,
								);

								if (associatedSizeVariable === undefined) {
									throw new Error(
										`Size variable "${config.size}" is not defined`,
									);
								}

								if (associatedSizeVariable.content.type !== "number") {
									throw new Error(
										`Size variable "${config.size}" is not a number`,
									);
								}

								const maximumSize = associatedSizeVariable.content.max;

								if (config?.multiple) {
									return languages?.map((language, index) => {
										const name = `${config.size}_${language.toLocaleLowerCase()}`;
										const multipleData = currentData[index] ?? "";
										if (typeof multipleData !== "string")
											throw Error("invalid string value for multiple data");

										const iconLanguageIndex = hasImage ? (
											<FontAwesomeIcon
												className={classNames(css.iconEye, {
													[css.iconEyeActive]: languageIndex == index,
												})}
												icon={faEye}
												onClick={() => setLanguageIndex(index)}
											/>
										) : null;

										return (
											<DataField
												index={index}
												content={multipleData}
												config={config}
												label={`${config.label} ${language}`}
												onChange={setDataContent(config.size, index, name)}
												maximumSize={maximumSize}
												disabled={!isEditing}
												tooltipText={`${config.label} ${language}`}
												iconLanguageIndex={iconLanguageIndex}
											/>
										);
									});
								}

								const tooltipText =
									typeof associatedSizeVariable.description == "string"
										? associatedSizeVariable.description
										: associatedSizeVariable.description[i18n.language] ||
											associatedSizeVariable.description["en"];

								const message: string = Array.isArray(
									memoryScratchPad.metadata.data[config.link],
								)
									? memoryScratchPad.metadata.data[config.link][languageIndex]
									: (memoryScratchPad.metadata.data[config.link] as string);

								return config.type !== "file" ? (
									<DataField
										index={index}
										content={currentData}
										config={config}
										label={config.label}
										onChange={setDataContent(config.size)}
										maximumSize={maximumSize}
										disabled={!isEditing}
										tooltipText={tooltipText}
									/>
								) : (
									<DataImageField
										index={index}
										content={currentData}
										onChange={setDataImageContent(config.size)}
										maximumSize={maximumSize}
										disabled={!isEditing}
										block={memoryScratchPad as Memory}
										tooltipText={tooltipText}
										message={message}
										config={logics.memory[selectedCategory ?? ""]}
									/>
								);
							})}
						</>
					)}
					<div className={css.row}>
						<div className={css.sectionTitle}>Metadata</div>
					</div>
					<div className={css.row}>
						<div className={css.label}>Device address</div>
						{isEditing && (
							<select
								id="select-device-memory"
								className={css.select}
								value={
									memoryScratchPad.source
										? memoryScratchPad.source.type === "local"
											? "Local"
											: memoryScratchPad.source.deviceId
										: ""
								}
								onChange={(event) => {
									setSourceDeviceId(event.target.value);
								}}
							>
								{memoryScratchPad.source === null && (
									<option value={""}></option>
								)}
								{"local" in config.modifiers && config.modifiers.local && (
									<option value={"null"}>Local</option>
								)}
								{editorDevices
									.filter(nonNullableFilter)
									.filter((device) => device.address)
									.map((device) => (
										<option key={device.id} value={device.id}>
											{device.address}
										</option>
									))}
							</select>
						)}
						{!isEditing && (
							<div className={css.variableValue}>
								{memoryScratchPad.source?.type === "local"
									? "Local"
									: editorDevices.find(
											(device) =>
												device?.id === memoryScratchPad.source?.deviceId,
										)?.address}
							</div>
						)}
					</div>
					{memoryScratchPad.source?.type === "io" && (
						<div className={css.row}>
							<div className={css.label}>ID</div>
							{isEditing && (
								<select
									id="select-io-memory"
									className={css.select}
									value={memoryScratchPad.source.io}
									onChange={(event) => {
										setSourceIo(event.target.value);
									}}
								>
									{memoryScratchPad.source.io === "" && (
										<option value={""}></option>
									)}
									{memoryScratchPad.name === "INPUT" &&
										(
											editorDevices.find(
												(device) =>
													device?.id ===
													(memoryScratchPad.source as MemorySourceIo).deviceId,
											)?.inputs ?? []
										).map((io) => {
											const other = editorMemories[memoryScratchPad.name].find(
												(memory) =>
													memory.source?.type === "io" &&
													memory.source.io === io.name &&
													memory.source?.deviceId ===
														(memoryScratchPad.source as MemorySourceIo)
															.deviceId,
											);
											return (
												<option key={io.name} value={io.name}>
													{io.name}
													{other ? ` [${other.label}]` : ""}
												</option>
											);
										})}
									{memoryScratchPad.name === "OUTPUT" &&
										(
											editorDevices.find(
												(device) =>
													device?.id ===
													(memoryScratchPad.source as MemorySourceIo).deviceId,
											)?.outputs ?? []
										).map((io) => {
											const other = editorMemories[memoryScratchPad.name].find(
												(memory) =>
													memory.source?.type === "io" &&
													memory.source.io === io.name &&
													memory.source?.deviceId ===
														(memoryScratchPad.source as MemorySourceIo)
															.deviceId,
											);
											return (
												<option key={io.name} value={io.name}>
													{io.name}
													{other ? ` [${other.label}]` : ""}
												</option>
											);
										})}
								</select>
							)}
							{!isEditing && (
								<div className={css.variableValue}>
									{(memoryScratchPad.source as MemorySourceIo).io}
								</div>
							)}
						</div>
					)}
					{memoryScratchPad.source?.type === "address" && (
						<div className={css.row}>
							<div className={css.label}>ID</div>
							{isEditing && (
								<NumberVariable
									content={{
										type: "number",
										value: memoryScratchPad.source.address,
									}}
									config={{
										content: {
											default: 0,
											type: "number",
											min: 0,
											max: 2 ** 16 - 1,
										},
										description: "",
										label: "",
										name: "",
										private: false,
									}}
									onChange={(value) => setSourceAddress(Number(value.value))}
								/>
							)}
							{!isEditing && (
								<div className={css.variableValue}>
									{memoryScratchPad.source.address}
								</div>
							)}
						</div>
					)}
				</div>
			)}
		</div>
	);
}

function MemoryLabel(props: {
	scratchPadMemory: MemoryScratchPad;
	onChange: (content: string) => void;
}) {
	return (
		<input
			className={css.memoryLabel}
			value={props.scratchPadMemory.label}
			autoFocus={true}
			onFocus={(event) => {
				event.target.select();
				event.target.style.width = "0px";
				const boundingRect = event.target.getBoundingClientRect();
				const size = Math.min(
					event.target.scrollWidth + 2 - boundingRect.width,
					300,
				);
				event.target.style.width = `${size}px`;
			}}
			onChange={(event) => {
				event.target.style.width = "0px";
				const boundingRect = event.target.getBoundingClientRect();
				const size = Math.min(
					event.target.scrollWidth + 2 - boundingRect.width,
					300,
				);
				event.target.style.width = `${size}px`;
				props.onChange(event.target.value);
			}}
		/>
	);
}
